/**
 * Peptide-evaluation step — post-purchase drawer step for
 * `consult-upsell-v1` variant-a (eligibility resolved server-side on
 * /me/post-purchase-offer → consult_upsell). Renders in the plan step's
 * slot when the quarterly step isn't showing, and after it resolves when
 * it is.
 *
 * ONE-TIME purchase, deliberately no subscription (CEO framing: "drop
 * the ongoing monthly/quarterly relationship"). Copy follows the CEO's
 * spec with goal words swapped to lawyer-reviewed phrasing (noholabs.com
 * /peptides vocabulary): "post-exercise recovery" not "injury recovery",
 * "healthy aging" not "anti-aging", "support"-style hedges — value
 * props, never compound names or condition-treatment claims; the
 * provider decides what's clinically appropriate on the visit.
 * Accepting one-click-charges the saved card; scheduling the 20-minute
 * visit is ops-manual (we text to book), so the confirmation promises
 * the text, not a calendar.
 *
 * Visual language matches the rest of the drawer (mono uppercase, the
 * #f5f5f5 title block, quiet fine print).
 */

import type { ConsultUpsell } from '@/lib/api/upsellHooks';

function fmtDollars(cents: number): string {
  const v = cents / 100;
  return v % 1 === 0 ? `$${v}` : `$${v.toFixed(2)}`;
}

const INCLUDES: { title: string; detail: string }[] = [
  {
    title: '20-minute provider visit',
    detail: 'Review your goals, health history, and treatment options.',
  },
  {
    title: 'Custom peptide protocol',
    detail:
      'Your provider will evaluate which peptides may be right for you and create a personalized plan.',
  },
];

interface Props {
  consult: ConsultUpsell;
  state: 'offer' | 'accepted' | 'unavailable';
  accepting: boolean;
  error: string | null;
  onAccept: () => void;
  onDecline: () => void;
  onContinue: () => void;
  /** Whether treatment upsell cards follow this step — changes the
   *  accepted-state button copy ("Continue" vs "Done"). */
  hasTreatmentsNext: boolean;
}

export function ConsultUpsellStep({
  consult,
  state,
  accepting,
  error,
  onAccept,
  onDecline,
  onContinue,
  hasTreatmentsNext,
}: Props) {
  const price = fmtDollars(consult.price_cents);

  // Fake-door landing (demand test): the click "processed", nothing was
  // charged, and the excuse is state availability. Same chrome as the
  // accepted screen so the flow feels finished, not broken.
  if (state === 'unavailable') {
    return (
      <div className="flex flex-col w-full">
        <div className="bg-[#f5f5f5] flex flex-col gap-8 px-15 pt-15 pb-15 w-full">
          <p className="font-mono text-22 md:text-24 text-black uppercase tracking-[-0.48px] m-0 leading-110">
            Sorry — not available
            <br />
            in your state yet
          </p>
          <p className="leading-130 text-14 md:text-16 text-[#3a3a3a] m-0">
            Provider peptide evaluations aren&rsquo;t available in your
            state just yet. <strong>You weren&rsquo;t charged.</strong>{' '}
            We&rsquo;ll let you know as soon as they open up near you.
          </p>
        </div>
        <div className="px-15 py-15">
          <button
            type="button"
            onClick={onContinue}
            className="w-full bg-black text-white font-mono text-14 uppercase tracking-[-0.28px] py-14 hover:bg-[#2a2a2a]"
          >
            {hasTreatmentsNext ? 'Continue' : 'Done'}
          </button>
        </div>
      </div>
    );
  }

  if (state === 'accepted') {
    return (
      <div className="flex flex-col w-full">
        <div className="bg-[#f5f5f5] flex flex-col gap-8 px-15 pt-15 pb-15 w-full">
          <p className="font-mono text-22 md:text-24 text-black uppercase tracking-[-0.48px] m-0 leading-110">
            You&rsquo;re booked in —
            <br />
            peptide evaluation
          </p>
          <p className="leading-130 text-14 md:text-16 text-[#3a3a3a] m-0">
            Payment received. Schedule your 20-minute provider visit from
            your home screen whenever you&rsquo;re ready — or we&rsquo;ll
            text you within 1 business day to get it booked. Come with your
            goals, leave with a personalized peptide protocol.
          </p>
        </div>
        <div className="px-15 py-15">
          <button
            type="button"
            onClick={onContinue}
            className="w-full bg-black text-white font-mono text-14 uppercase tracking-[-0.28px] py-14 hover:bg-[#2a2a2a]"
          >
            {hasTreatmentsNext ? 'Continue' : 'Done'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full">
      {/* Title block — same chrome as the other steps. Copy = CEO spec. */}
      <div className="bg-[#f5f5f5] flex flex-col gap-8 px-15 pt-15 pb-12 w-full">
        <p className="font-mono text-22 md:text-24 text-black uppercase tracking-[-0.48px] m-0 leading-110">
          Add a peptide
          <br />
          evaluation
        </p>
        <p className="leading-130 text-14 md:text-16 text-[#3a3a3a] m-0">
          Meet with a licensed provider to build a custom protocol around
          your goals — post-exercise recovery, healthy aging,
          healthy-looking skin, cognitive focus, sleep quality, and more.
        </p>
      </div>

      {/* INCLUDES rows */}
      <div className="flex flex-col w-full border-b border-[#ececec]">
        <div className="bg-[#f0f0f0] border-b border-[#ececec] px-15 py-8 font-mono text-11 uppercase tracking-[-0.24px] text-[#5e5e5e]">
          Includes
        </div>
        {INCLUDES.map((b, idx) => (
          <div
            key={b.title}
            className={`flex items-start gap-10 px-15 py-12 ${
              idx > 0 ? 'border-t border-[#ececec]' : ''
            }`}
          >
            <span className="font-mono text-13 text-black leading-tight">
              +
            </span>
            <div className="flex flex-col gap-3">
              <span className="font-mono text-13 text-black uppercase leading-tight">
                {b.title}
              </span>
              <span className="font-mono text-11 text-[#8a8a8a] uppercase leading-130">
                {b.detail}
              </span>
            </div>
          </div>
        ))}
        <div className="flex items-center justify-between px-15 py-14 bg-[#fafafa] border-t border-[#ececec]">
          <span className="font-mono text-13 text-black uppercase leading-tight">
            One-time visit
          </span>
          <span className="font-mono text-14 text-black">{price}</span>
        </div>
      </div>

      {/* CTAs */}
      <div className="flex flex-col gap-10 px-15 py-15">
        <button
          type="button"
          onClick={onAccept}
          disabled={accepting}
          className="w-full bg-black text-white font-mono text-14 uppercase tracking-[-0.28px] py-14 hover:bg-[#2a2a2a] disabled:opacity-60"
        >
          {accepting ? 'Booking…' : `Add peptide evaluation — ${price}`}
        </button>
        <button
          type="button"
          onClick={onDecline}
          disabled={accepting}
          className="w-full font-mono text-12 text-[#5e5e5e] uppercase underline py-6"
        >
          No thanks
        </button>
        {error && (
          <p className="font-mono text-12 text-[#b3261e] leading-130 m-0">
            {error}
          </p>
        )}
      </div>

      {/* Fine print — one-time charge, no subscription; scheduling is a
          follow-up text; Noho-style clinical hedge (prescribing only if
          appropriate — the visit isn't a prescription guarantee). */}
      <p className="px-15 pb-15 text-11 text-[#8a8a8a] leading-140 m-0">
        One-time {price} charge to your saved card — not a subscription.
        After purchase, we&rsquo;ll text you within 1 business day to
        schedule your visit. Treatments are recommended or prescribed
        following your evaluation only if clinically appropriate.
      </p>
    </div>
  );
}
